import { FC } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import type { MonthlyMarginRow } from '../margin.types';
import { FORECASTING_THEME, STAGE_COLORS } from '../forecastingTheme';
import { formatCurrency, formatPercent } from '../utils/forecastingAggregations';
import { ChartPlot, ChartTitle, Legend, Panel } from '../Forecasting.styles';
import { currencyAxisProps, maxOf } from '../utils/currencyAxis';
import { useShellPalette } from '@/styles';

interface MonthlyMarginChartProps {
  data: MonthlyMarginRow[];
  title?: string;
}

export const MonthlyMarginChart: FC<MonthlyMarginChartProps> = ({ data, title }) => {
  /* Resolved colours, not CSS variables: Recharts writes these into SVG
     attributes, where var() is not reliably supported. */
  const shell = useShellPalette();

  return (
    <Panel>
      <ChartTitle>{title ?? 'B + P | GP and margin by month'}</ChartTitle>
      <Legend>
        <span>
          <i style={{ background: STAGE_COLORS.Booked }} />
          B + P
        </span>
        <span>
          <i style={{ background: STAGE_COLORS.Pipe }} />
          GP
        </span>
        <span>
          <i
            style={{
              display: 'inline-block',
              width: 14,
              height: 0,
              borderTop: `2px solid ${FORECASTING_THEME.forecast}`,
              background: 'transparent',
              verticalAlign: 'middle'
            }}
          />
          Margin
        </span>
      </Legend>
      <ChartPlot>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={data} margin={{ top: 16, right: 48, left: 4, bottom: 8 }} accessibilityLayer={false}>
            <CartesianGrid stroke={shell.chartGrid} vertical={false} />
            <XAxis
              dataKey="axisLabel"
              tick={{ fill: shell.dataText, fontSize: 10, fontWeight: 400 }}
              axisLine={{ stroke: shell.chartAxisLine }}
              tickLine={false}
            />
            <YAxis
              yAxisId="amount"
              tick={{ fill: shell.dataText, fontSize: 11, fontWeight: 400 }}
              axisLine={false}
              tickLine={false}
              {...currencyAxisProps(maxOf(data, ['bookedPlusPipe', 'gp']))}
              width={52}
            />
            <YAxis
              yAxisId="margin"
              orientation="right"
              domain={[0, 100]}
              tick={{ fill: shell.dataText, fontSize: 11, fontWeight: 400 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `${v}%`}
              width={44}
            />
            <Tooltip
              formatter={(value: number, name: string) => {
                if (name === 'Margin') return [formatPercent(value, 0), name];
                return [formatCurrency(value), name];
              }}
              contentStyle={{
                background: '#1a2550',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 6,
                fontSize: 12
              }}
              labelStyle={{ color: '#fff' }}
            />
            <Bar
              yAxisId="amount"
              dataKey="bookedPlusPipe"
              name="B + P"
              fill={STAGE_COLORS.Booked}
              radius={[2, 2, 0, 0]}
              maxBarSize={48}
              isAnimationActive={false}
            />
            <Bar
              yAxisId="amount"
              dataKey="gp"
              name="GP"
              fill={STAGE_COLORS.Pipe}
              radius={[2, 2, 0, 0]}
              maxBarSize={48}
              isAnimationActive={false}
            />
            <Line
              yAxisId="margin"
              type="monotone"
              dataKey="marginPct"
              name="Margin"
              stroke={FORECASTING_THEME.forecast}
              strokeWidth={2.5}
              dot={{ r: 4, fill: FORECASTING_THEME.forecast }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartPlot>
    </Panel>
  );
};
